import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import {
    ArrowBack,
    CheckCircle,
    Cancel,
    HourglassEmpty,
    ExpandMore,
    ExpandLess,
} from '@mui/icons-material';
import moment from 'moment';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';

import { fetcher, offerApi } from '../../../lib/api';
import formatWithThousandSeparator from 'utils/thousand_separator';
import i18n from '../../../locales/i18n';
import Card from 'components/Card/Card.js';
import CardHeader from 'components/Card/CardHeader.js';
import { useThemeMode } from '../../../theme/ThemeProvider';
import styles from './OfferStatistics.module.scss';

const StatCardSkeleton = () => (
    <Card style={{ margin: 0 }}>
        <CardHeader>
            <div className={styles.skeletonLabel} />
            <div className={styles.skeletonValue} />
            <div className={styles.skeletonSub} />
            <div className={styles.skeletonSub} style={{ width: '50%' }} />
        </CardHeader>
    </Card>
);

const ChartSkeleton = ({ height = 300 }) => (
    <div className={styles.skeletonChart} style={{ height }} />
);

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

const DESCRIPTION_LIMIT = 180;
const CHART_COLORS = ['#0088FE', '#f423c6', '#FFBB28', '#FF8042', '#A1FF3C', '#3D5CFF', '#A2D5FF'];
const DAY_SHORT_KEYS = [
    'day_short_mon',
    'day_short_tue',
    'day_short_wed',
    'day_short_thu',
    'day_short_fri',
    'day_short_sat',
    'day_short_sun',
];

const readCssVar = (name, fallback) => {
    if (typeof window === 'undefined') return fallback;
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
};

const OfferOverview = ({ offer, currentLang }) => {
    const { t } = useTranslation();
    const [descExpanded, setDescExpanded] = useState(false);

    if (!offer?.id) return null;

    const image =
        offer.offer_images?.length > 0
            ? offer.offer_images[0].image
            : offer.image || '/spara-placeholder.png';

    const title = currentLang === 'en' && offer.title_en ? offer.title_en : offer.title;
    const description =
        currentLang === 'en' && offer.description_en ? offer.description_en : offer.description;
    const descLong = description && description.length > DESCRIPTION_LIMIT;

    const statusConfig = (() => {
        if (!offer.approved) {
            return {
                label: t('offer.in_review'),
                color: 'var(--spara-warning-text-strong, #f59e0b)',
                bg: 'var(--spara-warning-bg, #fffbeb)',
                Icon: HourglassEmpty,
            };
        }
        if (offer.is_active) {
            return {
                label: t('filter.active'),
                color: 'var(--spara-success-text, #16a34a)',
                bg: 'var(--spara-success-soft, #f0fdf4)',
                Icon: CheckCircle,
            };
        }
        return {
            label: t('offer.inactive'),
            color: 'var(--spara-badge-fail-fg, #dc2626)',
            bg: 'var(--spara-danger-bg, #fef2f2)',
            Icon: Cancel,
        };
    })();

    const startDate = offer.start_date ? moment(offer.start_date).format('DD.MM.YYYY') : null;
    const endDate = offer.end_date ? moment(offer.end_date).format('DD.MM.YYYY') : null;
    const activeTimes = offer.torgid_offer_active_times || [];

    const isFullDay = (slot) =>
        slot.active24 ||
        (slot.start_time?.startsWith('00:00') && slot.end_time?.startsWith('23:59'));

    const isAlwaysActive = activeTimes.length === 7 && activeTimes.every(isFullDay);

    return (
        <div className={styles.overviewCard}>
            <img
                src={image}
                alt={title}
                className={styles.overviewImage}
                onError={(e) => {
                    e.target.src = '/spara-placeholder.png';
                }}
            />
            <div className={styles.overviewBody}>
                <div className={styles.overviewTopRow}>
                    <div>
                        {offer.discount && (
                            <span className={styles.discountBadge}>{offer.discount}</span>
                        )}
                        <h2 className={styles.overviewTitle}>{title}</h2>
                    </div>
                    <span
                        className={styles.statusBadge}
                        style={{ color: statusConfig.color, background: statusConfig.bg }}
                    >
                        <statusConfig.Icon style={{ fontSize: 14 }} />
                        {statusConfig.label}
                    </span>
                </div>

                {offer.torgid_company && (
                    <div className={styles.companyRow}>
                        <img
                            src={offer.torgid_company.logo || '/spara-placeholder.png'}
                            alt={offer.torgid_company.name}
                            className={styles.companyLogo}
                            onError={(e) => {
                                e.target.src = '/spara-placeholder.png';
                            }}
                        />
                        <span className={styles.companyName}>{offer.torgid_company.name}</span>
                    </div>
                )}

                {description && (
                    <div className={styles.descriptionBlock}>
                        <p className={styles.descriptionText}>
                            {descLong && !descExpanded
                                ? description.slice(0, DESCRIPTION_LIMIT) + '… '
                                : description + ' '}
                            {descLong && (
                                <button
                                    className={styles.showMoreBtn}
                                    onClick={() => setDescExpanded((v) => !v)}
                                >
                                    {descExpanded ? (
                                        <>
                                            <ExpandLess
                                                style={{ fontSize: 15, verticalAlign: 'middle' }}
                                            />
                                            {t('stats.show_less')}
                                        </>
                                    ) : (
                                        <>
                                            <ExpandMore
                                                style={{ fontSize: 15, verticalAlign: 'middle' }}
                                            />
                                            {t('stats.show_more')}
                                        </>
                                    )}
                                </button>
                            )}
                        </p>
                    </div>
                )}

                {activeTimes.length > 0 && (
                    <div className={styles.activeDaysBlock}>
                        <span className={styles.activeDaysLabel}>{t('stats.active_days')}</span>
                        {isAlwaysActive ? (
                            <span className={styles.alwaysActivePill}>
                                {t('stats.always_active')}
                            </span>
                        ) : (
                            <div className={styles.activeDaysGrid}>
                                {DAY_SHORT_KEYS.map((dayKey, index) => {
                                    const slot = activeTimes.find((slotItem) => slotItem.active_day === index);
                                    const isActive = !!slot;
                                    const is24 = isActive && isFullDay(slot);
                                    return (
                                        <div
                                            key={index}
                                            className={`${styles.dayChip} ${
                                                isActive
                                                    ? styles.dayChipActive
                                                    : styles.dayChipInactive
                                            }`}
                                        >
                                            <span className={styles.dayChipLabel}>{t(dayKey)}</span>
                                            {isActive && !is24 && (
                                                <span className={styles.dayChipTime}>
                                                    {slot.start_time}–{slot.end_time}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {(startDate || endDate) && (
                    <div className={styles.dateRow}>
                        {startDate && (
                            <span className={styles.datePill}>
                                {t('stats.from')}: <b>{startDate}</b>
                            </span>
                        )}
                        {endDate && (
                            <span className={styles.datePill}>
                                {t('stats.to')}: <b>{endDate}</b>
                            </span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const OfferStatistics = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { theme: resolvedTheme, isDark } = useThemeMode();
    const lang = i18n.language.toLowerCase();
    const currentLang = lang.includes('is') ? 'is' : 'en';

    const { data: offerByIdData } = useSWR(offerApi.getById(id), fetcher);
    const { data: report, isLoading: loadingReport, error: reportError } = useSWR(
        offerApi.getReport(id),
        fetcher
    );
    const offerById = offerByIdData?.results ?? {};
    const noReportAccess =
        reportError?.response?.status === 403 || reportError?.response?.status === 401;

    const [activityFilter, setActivityFilter] = useState('month');
    const [allActivity, setAllActivity] = useState([]);

    const chartColors = useMemo(
        () => ({
            tick: readCssVar('--spara-text-muted', isDark ? '#b6bfcf' : '#666'),
            grid: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
        }),
        [resolvedTheme, isDark]
    );

    useEffect(() => {
        if (report?.results) {
            const data = [...(report.results.activates_and_favourites || [])];
            data.sort((a, b) => new Date(a.name) - new Date(b.name));
            setAllActivity(data);
        }
    }, [report]);

    const offer = offerById;

    const agesData = report?.results?.ages
        ? Object.keys(report.results.ages).map((key) => ({
              name: key,
              value: report.results.ages[key],
          }))
        : [];

    const regionData = report?.results?.region_of_users ?? [];

    const last30Min = (() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return d.toISOString().slice(0, 10);
    })();

    const xMin = activityFilter === 'month' ? last30Min : allActivity[0]?.name ?? last30Min;

    const activityChartData = {
        labels: allActivity.map((d) => d.name),
        datasets: [
            {
                label: t('dash.opens'),
                data: allActivity.map((d) => d.opens),
                backgroundColor: '#0088FE',
                stack: 'a',
            },
            {
                label: t('dash.activates'),
                data: allActivity.map((d) => d.activates),
                backgroundColor: '#00C49F',
                stack: 'a',
            },
            {
                label: t('dash.favourites'),
                data: allActivity.map((d) => d.favourites),
                backgroundColor: '#cf0000',
                stack: 'a',
            },
        ],
    };

    const activityChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 600 },
        plugins: {
            legend: {
                position: 'bottom',
                labels: { color: chartColors.tick },
            },
        },
        scales: {
            x: {
                stacked: true,
                grid: { display: false },
                min: xMin,
                ticks: { color: chartColors.tick },
            },
            y: {
                stacked: true,
                beginAtZero: true,
                grid: { color: chartColors.grid },
                ticks: { color: chartColors.tick },
            },
        },
    };

    const agesChartData = {
        labels: agesData.map((d) => d.name),
        datasets: [
            {
                label: t('users'),
                data: agesData.map((d) => d.value),
                backgroundColor: '#0088FE',
                borderRadius: 4,
            },
        ],
    };

    const agesChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
            x: {
                grid: { display: false },
                ticks: { color: chartColors.tick },
            },
            y: {
                beginAtZero: true,
                grid: { color: chartColors.grid },
                ticks: { color: chartColors.tick },
            },
        },
    };

    const regionChartData = {
        labels: regionData.map((d) => d.name),
        datasets: [
            {
                data: regionData.map((d) => d.value),
                backgroundColor: CHART_COLORS,
                borderWidth: 0,
            },
        ],
    };

    const pieOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: { boxWidth: 12, color: chartColors.tick },
            },
        },
    };

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <button className={styles.backButton} onClick={() => navigate('/offers')}>
                    <ArrowBack fontSize="small" />
                    {t('back')}
                </button>
                <h1 className={styles.title}>{t('statistic')}</h1>
            </div>

            <OfferOverview offer={offer} currentLang={currentLang} />

            {noReportAccess ? (
                <Card style={{ margin: 0 }}>
                    <CardHeader>
                        <p className={styles.cardCategory}>{t('offer.no_access_stats_title')}</p>
                        <p className={styles.cardSub}>{t('offer.no_access_stats_message')}</p>
                    </CardHeader>
                </Card>
            ) : loadingReport ? (
                <>
                    <div className={styles.statsGrid}>
                        <StatCardSkeleton />
                        <StatCardSkeleton />
                        <StatCardSkeleton />
                        <StatCardSkeleton />
                    </div>
                    <div className={styles.chartsSection}>
                        <div className={styles.chartCard}>
                            <div
                                className={styles.skeletonLabel}
                                style={{ width: 160, marginBottom: 16 }}
                            />
                            <ChartSkeleton height={300} />
                        </div>
                        <div className={styles.piChartsGrid}>
                            <div className={styles.chartCard}>
                                <ChartSkeleton height={220} />
                            </div>
                            <div className={styles.chartCard}>
                                <ChartSkeleton height={220} />
                            </div>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div className={styles.statsGrid}>
                        <Card style={{ margin: 0 }}>
                            <CardHeader>
                                <p className={styles.cardCategory}>{t('stats.offer_opened')}</p>
                                <h3 className={styles.cardValue}>
                                    {formatWithThousandSeparator(
                                        (report?.results?.totals?.offer_opened || 0) +
                                            (report?.results?.totals?.offer_opened_externally || 0)
                                    )}
                                </h3>
                                <p className={styles.cardSub}>
                                    {t('stats.in_app')}:{' '}
                                    <b>
                                        {formatWithThousandSeparator(
                                            report?.results?.totals?.offer_opened || 0
                                        )}
                                    </b>
                                </p>
                                <p className={styles.cardSub}>
                                    {t('stats.with_link')}:{' '}
                                    <b>
                                        {formatWithThousandSeparator(
                                            report?.results?.totals?.offer_opened_externally || 0
                                        )}
                                    </b>
                                </p>
                            </CardHeader>
                        </Card>

                        <Card style={{ margin: 0 }}>
                            <CardHeader>
                                <p className={styles.cardCategory}>{t('stats.offer_activated')}</p>
                                <h3 className={styles.cardValue}>
                                    {formatWithThousandSeparator(report?.results?.activates || 0)}
                                </h3>
                                <p className={styles.cardSub}>
                                    {t('stats.app_activation_log')}:{' '}
                                    <b>
                                        {formatWithThousandSeparator(
                                            report?.results?.totals?.offer_activated || 0
                                        )}
                                    </b>
                                </p>
                                <p className={styles.cardSub}>
                                    {t('stats.promo_link_opened')}:{' '}
                                    <b>{report?.results?.totals?.offer_link || 0}</b>
                                </p>
                            </CardHeader>
                        </Card>

                        <Card style={{ margin: 0 }}>
                            <CardHeader>
                                <p className={styles.cardCategory}>{t('stats.favourites')}</p>
                                <h3 className={styles.cardValue}>
                                    {formatWithThousandSeparator(report?.results?.favourites || 0)}
                                </h3>
                                <p className={styles.cardSub}>
                                    {t('stats.added_to_favourites')}:{' '}
                                    <b>
                                        {formatWithThousandSeparator(
                                            report?.results?.totals?.offer_hearted || 0
                                        )}
                                    </b>
                                </p>
                                <p className={styles.cardSub}>
                                    {t('stats.removed_from_favourites')}:{' '}
                                    <b>
                                        {formatWithThousandSeparator(
                                            report?.results?.totals?.offer_unhearted || 0
                                        )}
                                    </b>
                                </p>
                            </CardHeader>
                        </Card>

                        <Card style={{ margin: 0 }}>
                            <CardHeader>
                                <p className={styles.cardCategory}>{t('stats.offer_shared')}</p>
                                <h3 className={styles.cardValue}>
                                    {formatWithThousandSeparator(
                                        report?.results?.totals?.offer_shared || 0
                                    )}
                                </h3>
                                {report?.results?.share_methods &&
                                    Object.keys(report.results.share_methods).length > 0 &&
                                    Object.entries(report.results.share_methods).map(
                                        ([method, count]) => (
                                            <p key={method} className={styles.cardSub}>
                                                {method}:{' '}
                                                <b>{formatWithThousandSeparator(count)}</b>
                                            </p>
                                        )
                                    )}
                                <p className={styles.cardSub}>
                                    {t('stats.company_opened')}:{' '}
                                    <b>
                                        {formatWithThousandSeparator(
                                            report?.results?.totals?.company_opened_from_offer || 0
                                        )}
                                    </b>
                                </p>
                            </CardHeader>
                        </Card>
                    </div>

                    {(report?.results?.sources &&
                        Object.keys(report.results.sources).length > 0) ||
                    (report?.results?.screens &&
                        Object.keys(report.results.screens).length > 0) ||
                    (report?.results?.platforms &&
                        Object.keys(report.results.platforms).length > 0) ? (
                        <div className={styles.statsGrid} style={{ marginTop: 16 }}>
                            {report?.results?.sources &&
                            Object.keys(report.results.sources).length > 0 ? (
                                <Card style={{ margin: 0 }}>
                                    <CardHeader>
                                        <p className={styles.cardCategory}>
                                            {t('stats.open_source')}
                                        </p>
                                        {Object.entries(report.results.sources).map(
                                            ([source, count]) => (
                                                <p key={source} className={styles.cardSub}>
                                                    {source}:{' '}
                                                    <b>{formatWithThousandSeparator(count)}</b>
                                                </p>
                                            )
                                        )}
                                    </CardHeader>
                                </Card>
                            ) : null}
                            {report?.results?.screens &&
                            Object.keys(report.results.screens).length > 0 ? (
                                <Card style={{ margin: 0 }}>
                                    <CardHeader>
                                        <p className={styles.cardCategory}>
                                            {t('stats.open_screen')}
                                        </p>
                                        {Object.entries(report.results.screens).map(
                                            ([screen, count]) => (
                                                <p key={screen} className={styles.cardSub}>
                                                    {screen}:{' '}
                                                    <b>{formatWithThousandSeparator(count)}</b>
                                                </p>
                                            )
                                        )}
                                    </CardHeader>
                                </Card>
                            ) : null}
                            {report?.results?.platforms &&
                            Object.keys(report.results.platforms).length > 0 ? (
                                <Card style={{ margin: 0 }}>
                                    <CardHeader>
                                        <p className={styles.cardCategory}>
                                            {t('stats.platform')}
                                        </p>
                                        {Object.entries(report.results.platforms).map(
                                            ([platform, count]) => (
                                                <p key={platform} className={styles.cardSub}>
                                                    {platform}:{' '}
                                                    <b>{formatWithThousandSeparator(count)}</b>
                                                </p>
                                            )
                                        )}
                                    </CardHeader>
                                </Card>
                            ) : null}
                        </div>
                    ) : null}

                    <div className={styles.chartsSection}>
                        <div className={styles.chartCard}>
                            <div className={styles.chartHeader}>
                                <h3 className={styles.chartTitle}>{t('dash.offer_activity')}</h3>
                                <div className={styles.filterRow}>
                                    <button
                                        className={`${styles.filterButton} ${
                                            activityFilter === 'all' ? styles.active : ''
                                        }`}
                                        onClick={() => setActivityFilter('all')}
                                    >
                                        {t('dash.from_beginning')}
                                    </button>
                                    <button
                                        className={`${styles.filterButton} ${
                                            activityFilter === 'month' ? styles.active : ''
                                        }`}
                                        onClick={() => setActivityFilter('month')}
                                    >
                                        {t('dash.last_30_days')}
                                    </button>
                                </div>
                            </div>
                            <div style={{ height: 300 }}>
                                <Bar
                                    key={`activity-${resolvedTheme}-${activityFilter}`}
                                    data={activityChartData}
                                    options={activityChartOptions}
                                />
                            </div>
                        </div>

                        <div className={styles.piChartsGrid}>
                            <div className={styles.chartCard}>
                                <h3 className={styles.chartTitle}>{t('dash.user_ages')}</h3>
                                <div style={{ height: 220 }}>
                                    <Bar
                                        key={`ages-${resolvedTheme}`}
                                        data={agesChartData}
                                        options={agesChartOptions}
                                    />
                                </div>
                            </div>

                            <div className={styles.chartCard}>
                                <h3 className={styles.chartTitle}>{t('dash.user_region')}</h3>
                                <div style={{ height: 220 }}>
                                    <Doughnut
                                        key={`region-${resolvedTheme}`}
                                        data={regionChartData}
                                        options={pieOptions}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default OfferStatistics;
